import { Fragment, useState } from 'react';
import classNames from 'classnames';
import { set } from 'lodash';

import { IconButton, InlineButton } from '../../components/button';
import { Input } from '../../components/form/input';
import { Icon } from '../../components/icon';
import type {
  Aggregation,
  AggregationExtendedType,
  CompositeFilterCondition,
  Field,
  FilteringAggregation,
  Metric,
  VariableDefinition,
} from '../types';
import { AggregationForm } from './aggregation-form';
import { CompositeFilterConditionForm } from './edit-filter';
import { SortableItemSimple } from '../components/sortable-item';
import { ensureUniqueKey, getPropertyKey } from './utils';
import { Dropdown, DropdownMenuItem, DropdownIconSizePx } from '../../components/dropdown';
import {
  AggregationOptions,
  aggregationTypeToAggregationExtendedType,
  buildDefaultAggregationFilter,
  generateAggregationProperty,
  getAggregationOptionLabelByType,
  getAggregationOptionsForFields,
  getDefaultAggregation,
} from '../utils/aggregation';

import { useEnsureFieldsExistGrouped } from './hooks/use-ensure-fields-exist';
import {
  getAggregationKeys,
  getAggregationPropertyKeys,
  isAggregationWithFilter,
  isFilteringAggregation,
  isKeyedAggregation,
} from '../pipeline/operation';
import { FieldOrGroup, isField } from '../pipeline/utils';

import form from '../../components/form/form.module.scss';

interface AggregationsEditorProps {
  aggregations: Aggregation[];
  fields: FieldOrGroup[];
  metrics: Metric[];
  relationName?: string;
  showNameInput?: boolean;
  maxAggregations?: number;
  setAggregations(aggregations: Aggregation[]): void;
  // If no empty state is provided, the editor will always show at least one aggregation
  empty?: (addAggregation: (aggregation: Aggregation) => void) => React.ReactNode;
  autoFocus?: boolean;
  excludeAggregationTypes?: AggregationExtendedType[];
  getMenuOptions?: (aggregation: Aggregation, idx: number) => DropdownMenuItem[];
  filterEnabled?: boolean;
  variableDefinitions: VariableDefinition[];
}

export const AggregationsEditor = (props: AggregationsEditorProps) => {
  const {
    aggregations,
    metrics,
    excludeAggregationTypes = [],
    relationName,
    autoFocus = false,
    getMenuOptions,
  } = props;

  const extendedFields = useEnsureFieldsExistGrouped(
    props.fields,
    getAggregationKeys(aggregations),
  );
  const baseFields = props.fields.filter(isField);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [highlightedIdx, setHighlightedIdx] = useState<number | null>(null);

  if (aggregations.length === 0 && props.empty === undefined) {
    const defaultAggregation = getDefaultAggregation(
      baseFields,
      metrics,
      relationName,
      getAggregationOptionsForFields(extendedFields).filter(
        ({ value }) => !excludeAggregationTypes.includes(value),
      ),
    );
    aggregations.push(set(defaultAggregation, 'property.name', ''));
  }

  const handleSetAggregation = (idx: number, aggregation: Aggregation) =>
    props.setAggregations([
      ...aggregations.slice(0, idx),
      aggregation,
      ...aggregations.slice(idx + 1),
    ]);

  const handleAddAggregation = (aggregation: Aggregation) => {
    const existingKeys = getAggregationPropertyKeys(aggregations);
    props.setAggregations([
      ...aggregations,
      {
        ...aggregation,
        property: {
          ...aggregation.property,
          key: ensureUniqueKey(existingKeys, aggregation.property.key),
        },
      },
    ]);
  };

  const handleRemoveAggregation = (index: number) => {
    props.setAggregations([...aggregations.slice(0, index), ...aggregations.slice(index + 1)]);

    setHighlightedIdx(null);
  };

  const handleDuplicateAggregation = (index: number) => {
    const aggregation = aggregations.at(index);
    if (!aggregation) {
      return;
    }

    const duplicatedAggregation = {
      ...aggregation,
      property: {
        ...aggregation.property,
        key: ensureUniqueKey(getAggregationPropertyKeys(aggregations), aggregation.property.key),
        name: aggregation.property.name ? `${aggregation.property.name} copy` : '',
      },
    };

    props.setAggregations([
      ...aggregations.slice(0, index + 1),
      duplicatedAggregation,
      ...aggregations.slice(index + 1),
    ]);

    setHighlightedIdx(index + 1);
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    const tempAggregations = [
      ...aggregations.slice(0, fromIndex),
      ...aggregations.slice(fromIndex + 1),
    ];
    props.setAggregations([
      ...tempAggregations.slice(0, toIndex),
      aggregations[fromIndex],
      ...tempAggregations.slice(toIndex),
    ]);

    setHighlightedIdx(null);
  };

  const canAddAggregation =
    props.maxAggregations === undefined || aggregations.length < props.maxAggregations;

  if (aggregations.length === 0 && props.empty !== undefined) {
    return props.empty(handleAddAggregation);
  }

  const sortable = aggregations.length > 1;

  return (
    <div className={form.formHorizontal}>
      {aggregations.map((aggregation, i, items) => (
        <Fragment key={i}>
          {i > 0 && <hr className={form.dashed} />}
          <SortableItemSimple
            scope={'aggregation'}
            index={i}
            draggedIndex={draggedIndex}
            setDraggedIndex={setDraggedIndex}
            onReorder={handleReorder}
            className={classNames({ [form.sortableRow]: sortable })}
            enabled={sortable}
            classNames={{
              dragBefore: form.dragBefore,
              dragAfter: form.dragAfter,
            }}>
            {sortable && <Icon name="DragHandle" size={10} className={form.sortHandle} />}
            <EditAggregation
              key={aggregation.property.key}
              aggregation={aggregation}
              baseFields={baseFields}
              extendedFields={extendedFields}
              metrics={metrics}
              autoFocus={highlightedIdx !== null ? highlightedIdx === i : autoFocus}
              isRemovable={aggregations.length > 1 || props.empty !== undefined}
              isHighlighted={highlightedIdx === i}
              showNameInput={props.showNameInput}
              setAggregation={(aggregation) => handleSetAggregation(i, aggregation)}
              onRemove={() => handleRemoveAggregation(i)}
              onDuplicateAggregation={() => handleDuplicateAggregation(i)}
              relationName={props.relationName}
              excludeAggregationTypes={excludeAggregationTypes}
              menuOptions={getMenuOptions?.(aggregation, i)}
              filterEnabled={props.filterEnabled}
              variableDefinitions={props.variableDefinitions}
            />
            {aggregation === items.at(-1) && isAggregationWithFilter(aggregation) && (
              <hr className={form.dashed} style={{ margin: 0 }} />
            )}
          </SortableItemSimple>
        </Fragment>
      ))}

      {canAddAggregation ? (
        <div>
          <InlineButton
            size="small"
            onClick={() => {
              const unusedAggregationOptions = AggregationOptions.filter(
                (option) =>
                  aggregations.every((agg) => {
                    const field = isKeyedAggregation(agg)
                      ? baseFields.find((f) => f.key === agg.key)
                      : undefined;
                    const type = aggregationTypeToAggregationExtendedType(agg.type, field);
                    return type !== option.value;
                  }) && !excludeAggregationTypes.includes(option.value),
              );
              handleAddAggregation(
                getDefaultAggregation(baseFields, metrics, relationName, unusedAggregationOptions),
              );
            }}>
            <Icon name="Plus" size={15} /> Summarization
          </InlineButton>
        </div>
      ) : null}
    </div>
  );
};

interface EditAggregationProps {
  aggregation: Aggregation;
  baseFields: Field[];
  extendedFields: FieldOrGroup[];
  metrics: Metric[];
  isRemovable: boolean;
  isHighlighted?: boolean;
  setAggregation(aggregation: Aggregation): void;
  showNameInput?: boolean;
  relationName?: string;
  onDuplicateAggregation(): void;
  onRemove?(): void;
  excludeAggregationTypes?: AggregationExtendedType[];
  autoFocus?: boolean;
  menuOptions?: DropdownMenuItem[];
  filterEnabled?: boolean;
  variableDefinitions: VariableDefinition[];
}

const EditAggregation = (props: EditAggregationProps) => {
  const {
    aggregation,
    baseFields,
    extendedFields,
    metrics,
    isRemovable,
    isHighlighted = false,
    showNameInput,
    relationName,
    excludeAggregationTypes = [],
    autoFocus = false,
    onRemove,
    setAggregation,
    menuOptions = [],
    filterEnabled = false,
  } = props;

  const defaultProperty = generateAggregationProperty(
    aggregation,
    baseFields,
    metrics,
    relationName,
  );
  const isDefaultProperty =
    aggregation.property.name === '' || aggregation.property.name === defaultProperty.name;

  const [name, setName] = useState(aggregation.property.name);

  const [manualPropertyEnabled, setManualPropertyEnabled] = useState(!isDefaultProperty);
  // Show default property as empty input unless it's been modified
  const [isPropertyDirty, setIsPropertyDirty] = useState(false);

  const shouldShowNameInput = showNameInput === true || manualPropertyEnabled || !isDefaultProperty;

  const menuItems: DropdownMenuItem[] = [...menuOptions];

  if (!shouldShowNameInput) {
    menuItems.push({
      label: 'Add custom name',
      onClick: () => setManualPropertyEnabled(true),
      icon: <Icon name="Edit3" size={DropdownIconSizePx} />,
    });
  }

  if (filterEnabled) {
    if (isAggregationWithFilter(aggregation)) {
      menuItems.push({
        label: 'Remove filter',
        onClick: () => setAggregation({ ...aggregation, filter: undefined }),
        icon: <Icon name="Filter" size={DropdownIconSizePx} />,
      });
    } else {
      menuItems.push({
        label: 'Add filter',
        disabled: !isFilteringAggregation(aggregation),
        tooltip: !isFilteringAggregation(aggregation)
          ? `Filter is not supported for ${getAggregationOptionLabelByType(aggregation.type) ?? aggregation.type} summarization`
          : undefined,
        onClick: () => {
          if (!isFilteringAggregation(aggregation)) {
            return;
          }

          setAggregation({
            ...aggregation,
            filter: buildDefaultAggregationFilter(aggregation, baseFields),
          });
        },
        icon: <Icon name="Filter" size={DropdownIconSizePx} />,
      });
    }
  }

  menuItems.push({
    label: 'Duplicate summarization',
    onClick: props.onDuplicateAggregation,
    icon: <Icon name="Copy" size={DropdownIconSizePx} />,
  });

  if (isRemovable) {
    menuItems.push({
      label: 'Remove summarization',
      onClick: onRemove,
      icon: <Icon name="Trash2" size={DropdownIconSizePx} />,
    });
  }

  const setAggregationPropertyName = () => {
    const propertyName = !name.length ? defaultProperty.name : name;

    setAggregation({
      ...aggregation,
      property: {
        key: getPropertyKey(aggregation.property, propertyName),
        name: propertyName,
      },
    });
  };

  const setFilter = (filter: CompositeFilterCondition) => {
    if (isFilteringAggregation(aggregation)) {
      setAggregation({ ...aggregation, filter });
    }
  };

  const handleAggregationChange = (updatedAggregation: Aggregation) => {
    const newDefaultProperty = generateAggregationProperty(
      updatedAggregation,
      baseFields,
      metrics,
      relationName,
    );
    const newAggregation: Aggregation | FilteringAggregation = {
      ...updatedAggregation,
      property:
        isDefaultProperty && !isPropertyDirty
          ? {
              name: newDefaultProperty.name,
              key: aggregation.property.key ?? newDefaultProperty.key,
            }
          : updatedAggregation.property,
    };

    if (isFilteringAggregation(aggregation) && isFilteringAggregation(newAggregation)) {
      newAggregation.filter = aggregation.filter;
    }

    setAggregation(newAggregation);
  };

  const menu =
    menuItems.length === 1 && isRemovable ? (
      <IconButton icon="Trash2" title="Remove aggregation" size="small" onClick={onRemove} />
    ) : (
      menuItems.length > 0 && (
        <Dropdown
          align="right"
          trigger={(isOpen, setIsOpen) => (
            <IconButton
              icon="MoreHorizontal"
              size="small"
              title="More..."
              type="gray"
              onClick={() => setIsOpen(!isOpen)}
            />
          )}
          items={menuItems}
        />
      )
    );

  return (
    <div className={classNames(form.formHorizontal, { [form.highlightByFadeIn]: isHighlighted })}>
      <AggregationForm
        accessory={menu}
        aggregation={aggregation}
        autoFocus={autoFocus}
        excludeAggregationTypes={excludeAggregationTypes}
        fields={baseFields}
        metrics={metrics}
        relationName={relationName}
        setAggregation={handleAggregationChange}
      />
      {shouldShowNameInput && (
        <div className={classNames(form.formRow)}>
          <label className={form.formLabel}>Column name</label>
          <Input
            type="text"
            autoFocus={autoFocus}
            value={isDefaultProperty && !isPropertyDirty ? '' : name}
            placeholder={
              generateAggregationProperty(aggregation, baseFields, metrics, relationName).name
            }
            onChange={(e) => {
              setIsPropertyDirty(true);
              setName(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setAggregationPropertyName();
              }
            }}
            onBlur={setAggregationPropertyName}
          />
        </div>
      )}

      {isAggregationWithFilter(aggregation) && (
        <>
          <label className={form.formLabel}>Filter before aggregating</label>
          <CompositeFilterConditionForm
            fields={extendedFields}
            fieldsForExpression={baseFields}
            condition={aggregation.filter}
            setCondition={setFilter}
            variables={props.variableDefinitions}
          />
        </>
      )}
    </div>
  );
};
